import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { adminJobsService } from '../services/adminJobsService';
import type { AdminJobFilters, AdminJobListResponse } from '../types/adminJobs';

const DEFAULT_PAGE_SIZE = 20;

export function useAdminFailedJobs(initialFilters: AdminJobFilters = {}) {
    const [data, setData] = useState<AdminJobListResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [filters, setFilters] = useState<AdminJobFilters>({
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
        order_by: 'created_at',
        order_direction: 'desc',
        ...initialFilters,
    });

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchJobs = useCallback(async (overrideFilters?: AdminJobFilters) => {
        setLoading(true);
        setError(null);
        try {
            const result = await adminJobsService.getAdminFailedJobs(overrideFilters ?? filters);
            setData(result);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to fetch failed jobs';
            setError(message);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);

    const setFilter = useCallback((key: keyof AdminJobFilters, value: AdminJobFilters[keyof AdminJobFilters]) => {
        setFilters(prev => {
            const updated = { ...prev, [key]: value };
            // Reset offset when filters change (except offset itself)
            if (key !== 'offset') {
                updated.offset = 0;
            }
            return updated;
        });
    }, []);

    const setFilterDebounced = useCallback((key: keyof AdminJobFilters, value: AdminJobFilters[keyof AdminJobFilters]) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setFilter(key, value);
        }, 300);
    }, [setFilter]);

    const goToPage = useCallback((page: number) => {
        const limit = filters.limit ?? DEFAULT_PAGE_SIZE;
        setFilters(prev => ({ ...prev, offset: page * limit }));
    }, [filters.limit]);

    const refetch = useCallback(() => {
        fetchJobs();
    }, [fetchJobs]);

    const currentPage = useMemo(() => {
        const limit = filters.limit ?? DEFAULT_PAGE_SIZE;
        return Math.floor((filters.offset ?? 0) / limit);
    }, [filters.limit, filters.offset]);

    const totalPages = useMemo(() => {
        if (!data) return 0;
        const limit = filters.limit ?? DEFAULT_PAGE_SIZE;
        return Math.ceil(data.total_count / limit);
    }, [data, filters.limit]);

    const hasMore = useMemo(() => currentPage < totalPages - 1, [currentPage, totalPages]);

    return {
        data,
        loading,
        error,
        filters,
        setFilter,
        setFilterDebounced,
        goToPage,
        refetch,
        currentPage,
        totalPages,
        hasMore,
    };
}
